import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { extractLanguageFromFilename, fetchS3BucketListing, getS3FileUrl } from '../../utils/s3';
import type { RootState } from '../store';

export type ScribeTraining = {
  name: string;
  displayName?: string;
  url: string;
  language: 'en-US' | 'fr-FR' | 'es-ES';
  size?: number;
  lastModified?: string;
};

/**
 * Fetches the list of training files from S3 bucket dynamically
 */
export const fetchScribeTrainings = createAsyncThunk('scribeDashboard/fetchScribeTrainings', async () => {
  const s3Files = await fetchS3BucketListing();

  const trainings: ScribeTraining[] = s3Files.map((file) => ({
    name: file.key,
    displayName: file.key,
    url: getS3FileUrl(file.key),
    language: extractLanguageFromFilename(file.key) as 'en-US' | 'fr-FR' | 'es-ES',
    size: file.size,
    lastModified: file.lastModified,
  }));

  return trainings;
});

export const scribeDashboardRoomsUpdate = createAsyncThunk(
  'scribeDashboard/scribeDashboardRoomsUpdate',
  async (
    { subtype, room }: { subtype: 'add-room' | 'room-status-update' | 'remove-room' | string; room: any },
    { dispatch }
  ) => {
    if (subtype === 'add-room') {
      dispatch(scribeDashboardAddRoom(room));
    } else if (subtype === 'room-status-update') {
      dispatch(scribeDashboardRoomsUpdate(room));
    } else if (subtype === 'remove-room') {
      dispatch(scribeDashboardRemoveRoom(room));
    }
  }
);

export type State = {
  rooms: Array<any>;
  scribeUrl?: string;
  trainings: ScribeTraining[];
  trainingsLoading: boolean;
  trainingsError?: string;
  scribeTrainingRequested: boolean;
  scribeTrainingSourceNode?: MediaElementAudioSourceNode;
  scribeTrainingSelected?: ScribeTraining;
  scribeTrainingAudioStream?: MediaStream;

  scribeSocket?: WebSocket;
};

const initialState: State = {
  trainings: [],
  trainingsLoading: false,
  trainingsError: undefined,
  rooms: [],
  scribeTrainingRequested: false,
  scribeSocket: undefined,
};

export const scribeDashboardSlice = createSlice({
  name: 'scribeDashboard',
  initialState,
  reducers: {
    scribeDashboardWSUrl(state, { payload }: PayloadAction<string>) {
      state.scribeUrl = payload;
    },
    scribeDashboardRequestTraining(state, { payload }: PayloadAction<ScribeTraining>) {
      state.scribeTrainingRequested = true;
      state.scribeTrainingSelected = payload;
    },
    scribeDashboardStreamReady(state, { payload }: PayloadAction<{ audioStream: MediaStream }>) {
      state.scribeTrainingAudioStream = payload.audioStream;
    },
    scribeDashboardRoomStatusUpdate(state, { payload }: PayloadAction<any>) {
      const roomsById = Object.fromEntries(state.rooms.map((v) => [v.id, v]));
      const rooms = Object.values({ ...roomsById, [payload.id]: payload });
      state.rooms = rooms;
    },
    scribeDashboardAddRoom(state, { payload }: PayloadAction<any>) {
      state.rooms = state.rooms.filter((v) => v.id !== payload.id);
      state.rooms.push(payload);
    },
    scribeDashboardRemoveRoom(state, { payload }: PayloadAction<any>) {
      state.rooms = state.rooms.filter((v) => v.id !== payload.id);
    },
    setScribeSocket(state, { payload }) {
      state.scribeSocket = payload;
    },
    scribeDashboardClear(state) {
      state.rooms = [];
      state.scribeTrainingRequested = false;
      state.scribeTrainingSelected = undefined;
      state.scribeTrainingAudioStream = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchScribeTrainings.pending, (state) => {
      state.trainingsLoading = true;
      state.trainingsError = undefined;
    });
    builder.addCase(fetchScribeTrainings.fulfilled, (state, { payload }) => {
      state.trainings = payload;
      state.trainingsLoading = false;
      state.trainingsError = undefined;
    });
    builder.addCase(fetchScribeTrainings.rejected, (state, { error }) => {
      state.trainingsLoading = false;
      state.trainingsError = error.message || 'Failed to fetch training files';
      console.error('fetchScribeTrainings.rejected', error);
    });
  },
});

export const scribeDashboardReducer = scribeDashboardSlice.reducer;
export const {
  scribeDashboardAddRoom,
  scribeDashboardRemoveRoom,
  scribeDashboardRoomStatusUpdate,
  scribeDashboardRequestTraining,
  scribeDashboardStreamReady,
  scribeDashboardWSUrl,
  scribeDashboardClear,
} = scribeDashboardSlice.actions;

const { setScribeSocket } = scribeDashboardSlice.actions;

export const createScribeSocket = createAsyncThunk(
  'v1Session/createScribeSocket',
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState;
    const scribeUrl = state.scribeDashboard.scribeUrl;
    if (!scribeUrl) throw new Error('no scribe url');
    const ws = new WebSocket(scribeUrl);
    dispatch(setScribeSocket(ws));
    ws.addEventListener('message', (data) => {
      const message = JSON.parse(data.data);
      dispatch(scribeDashboardRoomsUpdate({ room: message.status || { id: message.roomId }, subtype: message.type }));
    });
    return ws;
  }
);
